import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Typography,
} from '@material-ui/core';
import { createElement, FC, Fragment, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useGetIsPC } from '../../../../../utilityHooks/useGetIsPC';
import CreativeFormatItem from '../../../../component/compound/BasicProjectSettingEditor/CreativeFormatItem';
import SelectCreativeTypes from '../../../../component/compound/BasicProjectSettingEditor/SelectCreativeTypes';
import SelectNetworksAndPlacements from '../../../../component/compound/BasicProjectSettingEditor/SelectNetworksAndPlacements';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../../constant/spacing';
import mq from '../../../../styleUtility/mediaquery';
import { InputtedCreativeFormat } from '../../../../types/InputtedCreativeFormat';
import { InputtingCreativeFormat } from '../../../../types/InputtingCreativeFormat';
import { InputtingCreativeFormatsFilter } from '../../../../types/InputtingCreativeFormatsFilter';
import { useEditingProjectState } from '../../hooks/useEditingProjectState';

type Props = {
  creativeFormats: ReadonlyArray<InputtingCreativeFormat>;
  originalCreativeFormat: Readonly<InputtedCreativeFormat>;
  inputtingCreativeFormatsFilter: Readonly<InputtingCreativeFormatsFilter> | null;
  selectedPlacementIndex: ReadonlyArray<string | null> | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (creationSetIndex?: number) => void;
  className?: string;
};

const AddResizeFormatsDialog: FC<Props> = ({
  creativeFormats,
  originalCreativeFormat,
  inputtingCreativeFormatsFilter,
  selectedPlacementIndex,
  isOpen,
  onClose,
  onSubmit,
  className,
}) => {
  const {
    selectInputtingCreativeFormats,
    updateInputtingCreativeFormatSelectedPlaybackTime,
    setInputtingCreativeFormatsFilterByCreativeType,
    setInputtingCreativeFormatsFilterByNetworkAndPlacement,
    clearAllSelectedInputtingCreativeFormatsFilterByCreativeType,
    clearAllSelectedInputtingCreativeFormatsFilterByNetworkAndPlacement,
  } = useEditingProjectState();

  const isPC = useGetIsPC();
  const originalCreativeFormatId = originalCreativeFormat
    ? originalCreativeFormat.id || originalCreativeFormat.tmpid
    : undefined;

  const originalCreativeFormatPlaybackTime =
    originalCreativeFormat && originalCreativeFormat.creativeType.key === 'video'
      ? originalCreativeFormat.attribute.playbackTime!.seconds
      : null;

  // Originalで選択中のCreativeTypeのindexを抽出する
  // Resizeの選択ダイアログではCreativeTypeは変更させないためOriginalCreativeFormatのCreativeTypeで固定するため
  const selectedCreativeTypeIndex = inputtingCreativeFormatsFilter
    ? inputtingCreativeFormatsFilter.creativeTypes
        .map((creativeType, index) =>
          creativeType.creativeType.key === originalCreativeFormat?.creativeType.key ? index.toString() : null,
        )
        .filter((item) => item !== null)
    : null;

  // Resizeに該当するCreativeFormatをfilter
  // "同じCreativeType" かつ "選択中のPlaybackTimeを所持している" かつ "AspectRatioが入力されていること（0は除外）" が条件
  // Video の中でも "PlaybackTimeが存在しないCreativeFormat" が条件分岐をクリアするが基本存在しないのでスルーする
  const filteredList = useMemo(() => filteredCreativeFormats(creativeFormats, originalCreativeFormat), [
    creativeFormats,
    originalCreativeFormat,
  ]);

  const isSelected = filteredList.some((creativeFormat) => creativeFormat.selected);

  return (
    <Dialog onClose={onClose} open={isOpen} maxWidth="lg" fullScreen={!isPC} className={className}>
      <DialogTitle>
        <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.add_resize" />
      </DialogTitle>
      <DialogContent>
        <Selectors container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
          {inputtingCreativeFormatsFilter ? (
            <>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <InputLabel shrink>
                  <Typography variant="caption">
                    <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.creative_types" />
                  </Typography>
                </InputLabel>
                <SelectCreativeTypes
                  isDisabled
                  inputtingCreativeFormatsFilter={inputtingCreativeFormatsFilter}
                  selectedCreativeTypeIndex={selectedCreativeTypeIndex}
                  onChangeSelectedCreativeType={(value) =>
                    value === 'all'
                      ? clearAllSelectedInputtingCreativeFormatsFilterByCreativeType()
                      : setInputtingCreativeFormatsFilterByCreativeType(Number(value))
                  }
                  onMountFilter={() =>
                    setInputtingCreativeFormatsFilterByCreativeType(Number(selectedCreativeTypeIndex))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <InputLabel shrink>
                  <Typography variant="caption">
                    <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.placements" />
                  </Typography>
                </InputLabel>
                <SelectNetworksAndPlacements
                  inputtingCreativeFormatsFilter={inputtingCreativeFormatsFilter}
                  selectedPlacementIndex={selectedPlacementIndex}
                  onChangeSelectedNetworksAndPlacements={(value) =>
                    value === 'all'
                      ? clearAllSelectedInputtingCreativeFormatsFilterByNetworkAndPlacement()
                      : setInputtingCreativeFormatsFilterByNetworkAndPlacement(Number(value))
                  }
                />
              </Grid>
            </>
          ) : null}
        </Selectors>
        <FormControl fullWidth>
          <Formats container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={2}>
            {filteredList.every((item) => !item.show) && (
              <EmptyMessage>
                <Typography>
                  <FormattedMessage id="creative_format_editor.add_formats_dialog.empty_message" />
                </Typography>
              </EmptyMessage>
            )}
            {filteredList.map((creativeFormat, index) =>
              creativeFormat.show ? (
                <FlexWrap key={`filteredList-${index}`}>
                  <CreativeFormatItem
                    onClick={() => {
                      const index = creativeFormat.originalArrayKey!;

                      if (
                        creativeFormat.creativeType.key === 'video' &&
                        creativeFormat.playbackTime &&
                        originalCreativeFormat.attribute.playbackTime
                      ) {
                        updateInputtingCreativeFormatSelectedPlaybackTime({
                          index,
                          secondsList: [originalCreativeFormat.attribute.playbackTime.seconds],
                        });
                      }

                      selectInputtingCreativeFormats({
                        index,
                        resizeBaseId: originalCreativeFormatId,
                      });
                    }}
                    creativeFormatIndex={index}
                    creativeFormat={creativeFormat}
                    onChangePlaybackTime={updateInputtingCreativeFormatSelectedPlaybackTime}
                    originalCreativeFormatPlaybackTime={originalCreativeFormatPlaybackTime}
                    onChange={(e) =>
                      selectInputtingCreativeFormats({
                        index: Number(e.target.value),
                        resizeBaseId: originalCreativeFormatId,
                      })
                    }
                  />
                </FlexWrap>
              ) : null,
            )}
          </Formats>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button size="small" color="default" onClick={onClose}>
          <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.cancel" />
        </Button>
        <Button
          size="small"
          disabled={!isSelected}
          onClick={() => {
            onSubmit();
          }}
        >
          <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.submit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const filteredCreativeFormats = (
  _inputtingCreativeFormats: ReadonlyArray<InputtingCreativeFormat>,
  _inputtedCreativeFormat: InputtedCreativeFormat,
): ReadonlyArray<InputtingCreativeFormat> =>
  _inputtingCreativeFormats
    .map((format, index) => setOriginalArrayKey(format, index))
    .filter((inputtingCreativeFormat) => filterSameSeconds(inputtingCreativeFormat, _inputtedCreativeFormat))
    .filter((inputtingCreativeFormat) => filterSameCreativeType(inputtingCreativeFormat, _inputtedCreativeFormat))
    .filter((inputtingCreativeFormat) => filterHasAspectRatio(inputtingCreativeFormat));

// 配列の要素がフィルタリングされるため、元の key をマッピングしておく
// これをしないとRadioボタンでチェック時に、本来の index key の CreativeFormat が selected にならない
const setOriginalArrayKey = (format: InputtingCreativeFormat, index: number): InputtingCreativeFormat => {
  format.originalArrayKey = index;
  return format;
};

const filterHasAspectRatio = (inputtingCreativeFormat: InputtingCreativeFormat) => {
  if (!inputtingCreativeFormat.aspectRatioItem) {
    return false;
  }

  return (
    inputtingCreativeFormat.aspectRatioItem.aspectRatio.width *
      inputtingCreativeFormat.aspectRatioItem.aspectRatio.height !==
    0
  );
};

const filterSameSeconds = (
  inputtingCreativeFormat: InputtingCreativeFormat,
  inputtedCreativeFormat: InputtedCreativeFormat,
) => {
  if (inputtingCreativeFormat.playbackTime && inputtedCreativeFormat.attribute.playbackTime) {
    return inputtingCreativeFormat.playbackTime.availables.find(
      (playbackTimeItem) => playbackTimeItem.seconds === inputtedCreativeFormat.attribute.playbackTime!.seconds,
    );
  } else {
    return true;
  }
};

const filterSameCreativeType = (
  inputtingCreativeFormat: InputtingCreativeFormat,
  inputtedCreativeFormat: InputtedCreativeFormat,
) =>
  inputtingCreativeFormat.creativeType &&
  inputtingCreativeFormat.creativeType.key === inputtedCreativeFormat.creativeType.key;

const DialogContent = styled(_DialogContent)`
  min-width: 960px;
  padding: 0 24px 8px;

  ${mq('sp')} {
    min-width: auto;
  }
`;

const DialogActions = styled(_DialogActions)`
  ${mq('sp')} {
    flex-direction: column-reverse;

    > button {
      width: 100%;

      :not(:first-of-type) {
        margin-bottom: ${SPACING_UNIT}px;
        margin-left: 0;
      }
    }
  }
`;

const Selectors = styled(Grid)`
  position: sticky;
  top: 0;
  margin-bottom: ${SPACING_LARGE}px;
  background: #fff;
  z-index: 100;
`;

const Formats = styled(Grid)`
  width: 100%;
  margin-bottom: ${SPACING_REGULAR}px;
`;

const EmptyMessage = styled.div`
  padding: ${SPACING_REGULAR}px;
  text-align: center;
`;

const FlexWrap = styled.div`
  display: flex;
  width: 25%;
  padding: 8px;

  ${mq('sp')} {
    width: 50%;
    padding: 8px 16px;
  }
`;

export default AddResizeFormatsDialog;
